import React from 'react';
import PropTypes from 'prop-types';
import { uiState } from 'react-redux-ui-state';
import { withStyles } from '@material-ui/core';
import Storage from '@material-ui/icons/Storage';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Card from '@material-ui/core/Card';

import roleDisabled from 'services/roleDisabled/index';
import RBACDeleteButton from 'components/RBACWrapper/RBACDeleteButton/RBACDeleteButton';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import ORTypography from 'components/ORTypography/ORTypography';
import UserInstanceButtonItem from 'components/MissionCtrl/InstancesPage/InstanceItem/UserInstanceButtonItem/UserInstanceButtonItem';
import ORAlert from 'components/ORAlert/ORAlert';
import LoadingPlaceholder from 'components/LoadingPlaceholder/LoadingPlaceholder';
import AddDatabaseFormContainer from '../AddDatabaseForm/AddDatabaseFormContainer';
import FormToggleButton from '../FormToggleButton/FormToggleButton';
import PopoutItem from '../PopoutItem/PopoutItem';
import ORTable from '../ORTable/ORTable';
import { handleClickAway } from '../../services/handleClickAway/index';

const propTypes = {
  databases: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  classes: PropTypes.shape({}).isRequired,
  instanceId: PropTypes.string.isRequired,
  deleteInstanceDatabase: PropTypes.func.isRequired,
  setUiState: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  service: PropTypes.string.isRequired,
  deleteConfirmationDialogOpen: PropTypes.bool.isRequired,
  databaseToDelete: PropTypes.string.isRequired,
  instanceDatabaseIsLoading: PropTypes.bool.isRequired,
};

export function UnstyledDatabaseList({
  classes,
  databases,
  instanceId,
  deleteInstanceDatabase,
  setUiState,
  open,
  userRoles,
  page,
  rowsPerPage,
  service,
  deleteConfirmationDialogOpen,
  databaseToDelete,
  instanceDatabaseIsLoading,
}) {
  function handleChangePage(event, newPage) {
    setUiState({
      page: newPage,
    });
  }

  function handleDeleteConfirmation(dbName) {
    setUiState({
      deleteConfirmationDialogOpen: true,
      databaseToDelete: dbName,
    });
  }

  function handleDatabaseDeleteCancel() {
    setUiState({
      deleteConfirmationDialogOpen: false,
      databaseToDelete: '',
    });
  }

  function handleDatabaseDelete() {
    deleteInstanceDatabase(instanceId, service, databaseToDelete);
    setUiState({
      databaseToDelete: '',
      deleteConfirmationDialogOpen: false,
    });
  }

  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];
  let anchorEl;
  const handleClick = () => {
    setUiState({
      open: !open,
    });
  };

  const getAnchorEl = () => anchorEl;

  const setRef = (node) => {
    anchorEl = node;
  };

  const addDatabaseForm = (
    <AddDatabaseFormContainer instanceId={instanceId} service={service} closePopout={handleClick} />
  );

  const addDatabaseFormButton =
    databases.length === 0 ? null : (
      <FormToggleButton
        openForm={handleClick}
        setRef={setRef}
        label="ADD A NEW DATABASE"
        tooltip="Add Database"
        icon={<Storage />}
        disabled={roleDisabled(userRoles, allowedUserRoles)}
      />
    );

  const embeddedAddDatabaseForm =
    databases.length === 0 ? (
      <Card className={classes.embeddedFormContainer}>
        <AddDatabaseFormContainer service={service} instanceId={instanceId} />
      </Card>
    ) : null;

  const boldText = (text) => (
    <strong>
      {text}
      :&nbsp;
    </strong>
  );

  const databaseItems = databases
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .map((database) => {
      const dbName = database.name || database.db; // For keys CRDB uses name, Mongo uses db
      return (
        <TableRow key={dbName} style={{ verticalAlign: 'top' }}>
          <TableCell classes={{ root: classes.tableCellRoot }} align="left">
            <ORTypography className={classes.dbName} align="left" variant="h6">
              {dbName}
            </ORTypography>
            {service.toLowerCase() === 'mongodb' && (
              <div className={classes.dbDetails}>
                {database.collections && (
                  <React.Fragment>
                    {boldText('Collections')}
                    {database.collections}
                    <br />
                  </React.Fragment>
                )}
                {database.objects && (
                  <React.Fragment>
                    {boldText('Objects')}
                    {database.objects}
                    <br />
                  </React.Fragment>
                )}
                {database.indexes && (
                  <React.Fragment>
                    {boldText('Index Count')}
                    {database.indexes}
                    <br />
                  </React.Fragment>
                )}
                {database.indexSize && (
                  <React.Fragment>
                    {boldText('Index Size')}
                    {database.indexSize / 1024}
                    &nbsp;KiB
                    <br />
                  </React.Fragment>
                )}
                {database.dataSize && (
                  <React.Fragment>
                    {boldText('Data Size')}
                    {database.dataSize}
                    &nbsp;Bytes
                    <br />
                  </React.Fragment>
                )}
                {database.storageSize && (
                  <React.Fragment>
                    {boldText('Storage Size')}
                    {database.storageSize / 1024}
                    &nbsp;KiB
                    <br />
                  </React.Fragment>
                )}
                {database.avgObjSize && (
                  <React.Fragment>
                    {boldText('Avg Object Size')}
                    {database.avgObjSize}
                    &nbsp;Bytes
                    <br />
                  </React.Fragment>
                )}
                {database.fsTotalSize && (
                  <React.Fragment>
                    {boldText('File Size')}
                    {database.fsTotalSize / 1048576}
                    &nbsp;MiB
                  </React.Fragment>
                )}
              </div>
            )}
          </TableCell>
          <TableCell classes={{ root: classes.tableCellRootIcon }} align="right">
            <div className={classes.buttonContainer}>
              {service.toLowerCase() === 'mongodb' && (
                <div className={classes.tooltipContainer}>
                  <UserInstanceButtonItem
                    key="userforminstancebtn"
                    instanceService={service}
                    instanceId={instanceId}
                    parentDb={dbName}
                    noLabel
                    customClasses={{
                      popout: {
                        width: '40px',
                        height: '40px',
                        padding: '7px',
                        boxShadow: 'none',
                      },
                      buttonContainer: {
                        display: 'block',
                        maxWidth: '40px',
                      },
                      button: {
                        minWidth: 'unset',
                        marginBottom: '1rem',
                      },
                    }}
                  />
                </div>
              )}
              <div>
                <RBACDeleteButton
                  tooltip="Delete Database"
                  size="small"
                  userAccess={userRoles}
                  allowedAccess={allowedUserRoles}
                  onClick={() => handleDeleteConfirmation(dbName)}
                />
              </div>
            </div>
          </TableCell>
        </TableRow>
      );
    });

  const databaseTable =
    databases.length === 0 ? null : (
      <div className={classes.tableWrapper}>
        <ORTable
          count={databases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
        >
          <TableBody>
            {databaseItems}
            {instanceDatabaseIsLoading ? (
              <TableRow>
                <LoadingPlaceholder />
              </TableRow>
            ) : null}
          </TableBody>
        </ORTable>
      </div>
    );

  return (
    <div className={classes.listContainer}>
      <ORAlert
        open={deleteConfirmationDialogOpen}
        onOk={handleDatabaseDelete}
        onCancel={handleDatabaseDeleteCancel}
        message={`Please confirm that you want to delete the database ${databaseToDelete}. All data will be permanently lost.`}
      />
      {embeddedAddDatabaseForm}
      <div className={classes.addDatabaseButtonContainer}>
        &nbsp;
        <div className={classes.addDatabaseButtonWrapper}>
          {addDatabaseFormButton}
          <PopoutItem
            anchorEl={getAnchorEl}
            handleClick={handleClick}
            handleClickAway={(e) => handleClickAway(e, anchorEl, () => setUiState({ open: false }))}
            placement="bottom"
            open={open}
          >
            {addDatabaseForm}
          </PopoutItem>
        </div>
      </div>
      {databaseTable}
    </div>
  );
}

UnstyledDatabaseList.propTypes = propTypes;

const styles = (theme) => ({
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  tooltipContainer: {
    display: 'flex',
    alignSelf: 'flex-end',
  },
  dbName: {
    fontFamily: theme.typography.altTitle.fontFamily,
    fontWeight: theme.typography.altTitle.fontWeight,
    fontSize: '18px',
    color: '#231F20',
  },
  dbDetails: {
    lineHeight: '21px',
  },
  tableCellRoot: {
    width: '75%',
    padding: '7px 8px 7px 8px',
    fontSize: '12px !important',
  },
  tableCellRootIcon: {
    width: '25%',
    padding: '7px 8px 7px 8px',
  },
  embeddedFormContainer: {
    maxWidth: '900px',
    marginRight: 'auto',
    marginLeft: 'auto',
    boxShadow: '3px 5px 5px 3px rgba(216,216,216,1)',
  },
  listItemDeleteRoot: {
    color: theme.customColors.white,
    backgroundColor: theme.customColors.red,
  },
  listContainer: {
    position: 'relative',
  },
  addDatabaseButtonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    verticalAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
    },
  },
  addFormButtonContainer: {
    display: 'block',
  },
  tableWrapper: {
    '& th': {
      height: 'unset',
      padding: theme.spacing(1),
    },
    '& td': {
      padding: theme.spacing(1),
    },
  },
});

export const uiConfig = {
  name: 'userList',
  state: () => ({
    open: false,
    page: 0,
    rowsPerPage: 10,
    deleteConfirmationDialogOpen: false,
    databaseToDelete: '',
  }),
};

export default uiState(uiConfig)(withStyles(styles)(UnstyledDatabaseList));
